import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CookiePolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-bissa-blue hover:text-bissa-gold transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <h1 className="text-4xl font-bold text-bissa-blue mb-2">Cookie Policy</h1>
          <p className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-8 prose prose-lg max-w-none">
          <h2>1. What Are Cookies?</h2>
          <p>
            Cookies are small text files that are placed on your computer or mobile device when you visit 
            a website. They are widely used to make websites work more efficiently and provide information 
            to website owners about how users interact with their sites.
          </p>

          <h2>2. How We Use Cookies</h2>
          <p>
            Bissa.ai uses cookies and similar tracking technologies to enhance your experience, 
            analyze usage patterns, and provide personalized content. We use cookies for:
          </p>
          <ul>
            <li>Authentication and session management</li>
            <li>Remembering your preferences and settings</li>
            <li>Analytics and performance monitoring</li>
            <li>Security and fraud prevention</li>
            <li>Providing personalized content and features</li>
          </ul>

          <h2>3. Types of Cookies We Use</h2>
          
          <h3>Essential Cookies</h3>
          <p>
            These cookies are necessary for the website to function properly. They enable core functionality 
            such as security, network management, and accessibility.
          </p>
          <ul>
            <li><strong>Session cookies:</strong> Keep you logged in during your visit</li>
            <li><strong>Authentication cookies:</strong> Verify your identity and maintain security</li>
            <li><strong>Load balancing cookies:</strong> Ensure optimal website performance</li>
          </ul>

          <h3>Performance Cookies</h3>
          <p>
            These cookies collect information about how visitors use our website, helping us improve 
            performance and user experience.
          </p>
          <ul>
            <li><strong>Analytics cookies:</strong> Track page views, user interactions, and site usage</li>
            <li><strong>Error tracking cookies:</strong> Help us identify and fix technical issues</li>
            <li><strong>Performance monitoring:</strong> Monitor site speed and reliability</li>
          </ul>

          <h3>Functionality Cookies</h3>
          <p>
            These cookies remember your preferences and choices to provide a personalized experience.
          </p>
          <ul>
            <li><strong>Preference cookies:</strong> Remember your language and region settings</li>
            <li><strong>Feature cookies:</strong> Enable enhanced functionality and customization</li>
            <li><strong>UI state cookies:</strong> Remember your dashboard layout and preferences</li>
          </ul>

          <h3>Targeting/Advertising Cookies</h3>
          <p>
            We may use these cookies to deliver relevant advertising and measure campaign effectiveness.
          </p>
          <ul>
            <li><strong>Advertising cookies:</strong> Show relevant ads based on your interests</li>
            <li><strong>Marketing cookies:</strong> Track the effectiveness of our marketing campaigns</li>
            <li><strong>Social media cookies:</strong> Enable social sharing functionality</li>
          </ul>

          <h2>4. Third-Party Cookies</h2>
          <p>We work with trusted third-party services that may place cookies on your device:</p>
          
          <h3>Analytics Services</h3>
          <ul>
            <li><strong>Google Analytics:</strong> Website usage analytics and insights</li>
            <li><strong>Hotjar:</strong> User behavior analysis and heatmaps</li>
          </ul>

          <h3>Payment Processing</h3>
          <ul>
            <li><strong>Stripe:</strong> Secure payment processing and fraud prevention</li>
          </ul>

          <h3>Authentication Services</h3>
          <ul>
            <li><strong>Google OAuth:</strong> Social login functionality</li>
            <li><strong>Microsoft OAuth:</strong> Social login functionality</li>
          </ul>

          <h3>Customer Support</h3>
          <ul>
            <li><strong>Intercom:</strong> Customer support chat and communication</li>
          </ul>

          <h2>5. Cookie Duration</h2>
          <p>Cookies have different lifespans:</p>
          
          <h3>Session Cookies</h3>
          <p>
            Temporary cookies that are deleted when you close your browser. Used for essential 
            functionality and security.
          </p>

          <h3>Persistent Cookies</h3>
          <p>
            Remain on your device for a set period or until manually deleted. Used for remembering 
            preferences and improving user experience.
          </p>

          <h2>6. Managing Your Cookie Preferences</h2>
          
          <h3>Browser Settings</h3>
          <p>Most browsers allow you to control cookies through their settings:</p>
          <ul>
            <li>View and delete existing cookies</li>
            <li>Block cookies from specific sites</li>
            <li>Block third-party cookies</li>
            <li>Delete cookies when closing the browser</li>
            <li>Receive notifications before cookies are stored</li>
          </ul>

          <h3>Opt-Out Links</h3>
          <p>You can opt out of certain third-party cookies:</p>
          <ul>
            <li><a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out</a></li>
            <li><a href="https://www.hotjar.com/legal/compliance/opt-out" target="_blank" rel="noopener noreferrer">Hotjar Opt-out</a></li>
          </ul>

          <h3>Do Not Track</h3>
          <p>
            We respect Do Not Track signals. When you enable Do Not Track in your browser, 
            we will not track your behavior for analytics purposes.
          </p>

          <h2>7. Impact of Disabling Cookies</h2>
          <p>
            While you can browse our website with cookies disabled, some functionality may be limited:
          </p>
          <ul>
            <li>You may need to re-enter login information on each visit</li>
            <li>Personalized settings and preferences may not be saved</li>
            <li>Some features may not function properly</li>
            <li>We cannot provide personalized content or recommendations</li>
          </ul>

          <h2>8. Mobile App Tracking</h2>
          <p>
            Our mobile applications may use similar tracking technologies, including:
          </p>
          <ul>
            <li>Device identifiers and advertising IDs</li>
            <li>Local storage and cached data</li>
            <li>Analytics SDKs for usage tracking</li>
          </ul>

          <h2>9. Updates to This Cookie Policy</h2>
          <p>
            We may update this Cookie Policy to reflect changes in our practices or legal requirements. 
            We will notify you of significant changes by posting the updated policy on our website 
            and updating the "Last updated" date.
          </p>

          <h2>10. Contact Us</h2>
          <p>
            If you have questions about our use of cookies or this Cookie Policy, please contact us:
          </p>
          <ul>
            <li>Through the support system in your Bissa.ai dashboard</li>
            <li>By email at privacy@bissa.ai</li>
            <li>Through our contact form on the website</li>
          </ul>

          <div className="mt-12 p-6 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="text-blue-800 font-semibold mb-2">Cookie Transparency</h3>
            <p className="text-blue-700">
              We believe in transparency about our data collection practices. This policy explains 
              exactly how we use cookies to improve your experience while respecting your privacy choices.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;